import { configureStore } from "@reduxjs/toolkit";

import aiChatApi from "@/providers/store/api/ai-chat/ai-chat";
import aiGlossaryApi from "@/providers/store/api/ai-glossary/ai-glossary";
import districtMembersApi from "@/providers/store/api/district-members/district-members";
import driveApi from "@/providers/store/api/drive/drive";
import edlinkApi from "@/providers/store/api/edlink/edlink";
import findMySchoolApi from "@/providers/store/api/find-my-school/find-my-school";
import platformDistrictsApi from "@/providers/store/api/platform-districts/platform-districts";
import platformMembersApi from "@/providers/store/api/platform-members/platform-members";
import supportApi from "@/providers/store/api/support/support";

import { broadcastsApi } from "./api/admin/broadcasts";
import { chatAlertsApi } from "./api/admin/chat-alerts";
import { adminChatRoomsApi } from "./api/admin/chat-rooms";
import { adminUsersApi } from "./api/admin/users";
import authApi from "./api/auth/auth";
import customLayersApi from "./api/custom-layers/custom-layers";
import districtsApi from "./api/districts/districts";
import documentsApi from "./api/documents/documents";
import geocodingApi from "./api/geocoding/geocoding";
import importsApi from "./api/imports/imports";
import mapLevelsApi from "./api/map-levels/map-levels";
import mapSettingPresetsApi from "./api/map-setting-presets/map-setting-presets";
import projectsApi from "./api/projects/projects";
import reportsApi from "./api/reports/reports";
import sampleDataApi from "./api/sample-data/sample-data";
import schoolsApi from "./api/schools/schools";
import shapesApi from "./api/shapes/shapes";
import storageApi from "./api/storage/storage";
import studentsApi from "./api/students/students";
import usersApi from "./api/users/users";
import { sessionResetMiddleware } from "./session-reset.middleware";
import appReducer from "./slices/app/slice";
import districtReducer from "./slices/district/slice";
import userReducer from "./slices/user/slice";

export const makeStore = () => configureStore({
  reducer: {
    [authApi.reducerPath]: authApi.reducer,
    [customLayersApi.reducerPath]: customLayersApi.reducer,
    [mapLevelsApi.reducerPath]: mapLevelsApi.reducer,
    [usersApi.reducerPath]: usersApi.reducer,
    [districtsApi.reducerPath]: districtsApi.reducer,
    [schoolsApi.reducerPath]: schoolsApi.reducer,
    [sampleDataApi.reducerPath]: sampleDataApi.reducer,
    [projectsApi.reducerPath]: projectsApi.reducer,
    [studentsApi.reducerPath]: studentsApi.reducer,
    [documentsApi.reducerPath]: documentsApi.reducer,
    [storageApi.reducerPath]: storageApi.reducer,
    [geocodingApi.reducerPath]: geocodingApi.reducer,
    [importsApi.reducerPath]: importsApi.reducer,
    [reportsApi.reducerPath]: reportsApi.reducer,
    [districtMembersApi.reducerPath]: districtMembersApi.reducer,
    [edlinkApi.reducerPath]: edlinkApi.reducer,
    [platformMembersApi.reducerPath]: platformMembersApi.reducer,
    [platformDistrictsApi.reducerPath]: platformDistrictsApi.reducer,
    [aiChatApi.reducerPath]: aiChatApi.reducer,
    [aiGlossaryApi.reducerPath]: aiGlossaryApi.reducer,
    [chatAlertsApi.reducerPath]: chatAlertsApi.reducer,
    [adminUsersApi.reducerPath]: adminUsersApi.reducer,
    [adminChatRoomsApi.reducerPath]: adminChatRoomsApi.reducer,
    [broadcastsApi.reducerPath]: broadcastsApi.reducer,
    [shapesApi.reducerPath]: shapesApi.reducer,
    [driveApi.reducerPath]: driveApi.reducer,
    [mapSettingPresetsApi.reducerPath]: mapSettingPresetsApi.reducer,
    [findMySchoolApi.reducerPath]: findMySchoolApi.reducer,
    [supportApi.reducerPath]: supportApi.reducer,
    user: userReducer,
    app: appReducer,
    district: districtReducer,
  },
  devTools: process.env.NODE_ENV === "development",
  middleware: (getDefaultMiddleware) => getDefaultMiddleware({}).concat([
    authApi.middleware,
    customLayersApi.middleware,
    mapLevelsApi.middleware,
    usersApi.middleware,
    districtsApi.middleware,
    schoolsApi.middleware,
    sampleDataApi.middleware,
    projectsApi.middleware,
    studentsApi.middleware,
    documentsApi.middleware,
    storageApi.middleware,
    geocodingApi.middleware,
    importsApi.middleware,
    reportsApi.middleware,
    districtMembersApi.middleware,
    edlinkApi.middleware,
    platformMembersApi.middleware,
    platformDistrictsApi.middleware,
    shapesApi.middleware,
    aiChatApi.middleware,
    aiGlossaryApi.middleware,
    chatAlertsApi.middleware,
    adminUsersApi.middleware,
    adminChatRoomsApi.middleware,
    broadcastsApi.middleware,
    driveApi.middleware,
    mapSettingPresetsApi.middleware,
    findMySchoolApi.middleware,
    supportApi.middleware,
    sessionResetMiddleware,
  ]),
});

export type AppStore = ReturnType<typeof makeStore>

export type RootState = ReturnType<AppStore["getState"]>
export type AppDispatch = AppStore["dispatch"]
